import { useEffect, useState, useCallback, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { toast } from "sonner";
import { Coffee, RefreshCw, Filter, DollarSign, Ban } from "lucide-react";
import { SortableHeader, useSortableRows } from "../components/SortableTable";

/**
 * Read-only admin listing of guard breaks. Highlights paid vs unpaid so an
 * ops manager can quickly audit the payroll deduction impact for a window.
 */
export default function Breaks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [breakType, setBreakType] = useState("");  // "" | paid | unpaid

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/breaks", { params: { days } });
      setRows(r.data || []);
    } catch (e) { toast.error(errorMessage(e, "Load failed")); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const [siteMap, setSiteMap] = useState({});
  useEffect(() => {
    api.get("/sites").catch(() => ({ data: [] })).then((r) => {
      setSiteMap(Object.fromEntries((r.data || []).map((x) => [x.id, x.name])));
    });
  }, []);

  const decorated = useMemo(() => rows
    .filter((r) => !breakType || r.break_type === breakType)
    .map((r) => ({ ...r, site_name: siteMap[r.site_id] || r.site_id }))
  , [rows, siteMap, breakType]);

  const accessors = useMemo(() => ({
    start_at:         (r) => r.start_at || "",
    guard_name:       (r) => (r.guard_name || "").toLowerCase(),
    site_name:        (r) => (r.site_name || "").toLowerCase(),
    duration_seconds: (r) => r.duration_seconds || 0,
    break_type:       (r) => r.break_type || "",
  }), []);
  const sortState = useSortableRows(decorated, accessors, {
    defaultKey: "start_at", defaultDir: "desc", storageKey: "breaks",
  });
  const sorted = sortState.sorted;

  const totals = useMemo(() => {
    const paidMin = decorated.filter((r) => r.break_type === "paid")
      .reduce((s, r) => s + (r.duration_seconds || 0), 0) / 60;
    const unpaidMin = decorated.filter((r) => r.break_type === "unpaid")
      .reduce((s, r) => s + (r.duration_seconds || 0), 0) / 60;
    return { paid: Math.round(paidMin), unpaid: Math.round(unpaidMin), active: decorated.filter((r) => r.status === "active").length };
  }, [decorated]);

  const fmtDur = (sec) => {
    if (!sec) return "—";
    const m = Math.floor(sec / 60);
    if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
    return `${m}m ${sec % 60}s`;
  };

  return (
    <div>
      <PageHeader
        title="Breaks"
        subtitle="Officer breaks with paid/unpaid classification"
        action={
          <GhostButton onClick={load} disabled={loading} data-testid="breaks-refresh">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
        }
      />

      <div className="p-8 space-y-4">
        {/* Summary pills */}
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 border border-[#27272A] bg-[#0F0F0F] px-3 py-1.5" data-testid="breaks-total-paid">
            <DollarSign className="size-3.5 text-[#34C759]" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">PAID MIN</span>
            <span className="font-mono font-black text-sm text-[#34C759]">{totals.paid}</span>
          </div>
          <div className="flex items-center gap-2 border border-[#27272A] bg-[#0F0F0F] px-3 py-1.5" data-testid="breaks-total-unpaid">
            <Ban className="size-3.5 text-[#FF9F0A]" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">UNPAID MIN (DEDUCTED)</span>
            <span className="font-mono font-black text-sm text-[#FF9F0A]">{totals.unpaid}</span>
          </div>
          <div className="flex items-center gap-2 border border-[#27272A] bg-[#0F0F0F] px-3 py-1.5" data-testid="breaks-total-active">
            <Coffee className="size-3.5 text-[#0A84FF]" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">ACTIVE NOW</span>
            <span className="font-mono font-black text-sm text-[#0A84FF]">{totals.active}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 border border-[#27272A] p-3">
          <div className="flex items-center gap-2">
            <Filter className="size-3.5 text-zinc-500" />
            <span className="label-mute">Filters</span>
          </div>
          <div>
            <div className="label-mute mb-1">Window</div>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-sm outline-none focus:border-[#FF3B30]"
              data-testid="breaks-days"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
          </div>
          <div>
            <div className="label-mute mb-1">Type</div>
            <select
              value={breakType}
              onChange={(e) => setBreakType(e.target.value)}
              className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-sm outline-none focus:border-[#FF3B30]"
              data-testid="breaks-type-filter"
            >
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
        </div>

        <div className="border border-[#27272A]">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F] border-b border-[#27272A]">
              <tr>
                <SortableHeader k="start_at"         state={sortState} className="text-left px-3 py-2">Started</SortableHeader>
                <SortableHeader k="guard_name"       state={sortState} className="text-left px-3 py-2">Officer</SortableHeader>
                <SortableHeader k="site_name"        state={sortState} className="text-left px-3 py-2">Site</SortableHeader>
                <SortableHeader k="duration_seconds" state={sortState} className="text-left px-3 py-2">Duration</SortableHeader>
                <SortableHeader k="break_type"       state={sortState} className="text-left px-3 py-2">Type</SortableHeader>
                <th className="text-left px-3 py-2">Ended</th>
                <th className="text-left px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500">
                  {loading ? "Loading…" : "No breaks in this window."}
                </td></tr>
              )}
              {sorted.map((r) => (
                <tr key={r.id} className="border-b border-[#1F1F1F] hover:bg-[#0F0F0F]" data-testid={`break-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-400">
                    {r.start_at ? new Date(r.start_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">{r.guard_name || "—"}</td>
                  <td className="px-3 py-2">{r.site_name}</td>
                  <td className="px-3 py-2 font-mono">{fmtDur(r.duration_seconds)}</td>
                  <td className="px-3 py-2">
                    {r.status === "active" ? (
                      <PillBadge className="!text-[10px] !border-[#0A84FF] !text-[#0A84FF]">ACTIVE</PillBadge>
                    ) : r.break_type === "paid" ? (
                      <PillBadge className="!text-[10px] !border-[#34C759] !text-[#34C759]">PAID</PillBadge>
                    ) : (
                      <PillBadge className="!text-[10px] !border-[#FF9F0A] !text-[#FF9F0A]">UNPAID</PillBadge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-400 font-mono">
                    {r.end_at ? new Date(r.end_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-400 truncate max-w-[240px]">
                    {r.note || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
